import React, { useState, useEffect } from "react";
import { WhatsAppTemplate } from "../../types";
import {
  MessageSquare,
  Smartphone,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Send,
  Plus,
  Trash2,
  Loader2,
} from "lucide-react";
import {
  getWhatsappStatus,
  logoutWhatsapp,
  resetWhatsapp,
  WhatsappStatus,
} from "../../services/api/admin/whatsapp";
import Swal from "sweetalert2";

export const WhatsAppGateway: React.FC = () => {
  const [connStatus, setConnStatus] = useState<WhatsappStatus>({
    status: "DISCONNECTED",
    qr: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([
    {
      id: "1",
      name: "Reminder Pembayaran",
      trigger: "Pending (1 Hour)",
      content:
        "Halo {name}, tiketmu GIZ-{code} menunggu pembayaran. Segera lunasi sebelum hangus ya! 🐰",
    },
    {
      id: "2",
      name: "E-Ticket Terbit",
      trigger: "Payment Success",
      content:
        "Terima kasih {name}! Pembayaran lunas. Ini link E-Ticket kamu: gizpark.com/t/{code} ✨",
    },
    {
      id: "3",
      name: "Broadcast Promo Weekend",
      trigger: "Manual",
      content:
        "Weekend seru di RIW! Tunjukkan pesan ini dan dapatkan diskon 10% khusus hari ini. 🥕",
    },
  ]);

  const fetchStatus = async () => {
    try {
      const data = await getWhatsappStatus();
      setConnStatus(data);
    } catch (error) {
      console.error("Failed to fetch WA status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Poll status every 5 seconds
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleDisconnect = async () => {
    const result = await Swal.fire({
      title: "Putuskan Koneksi?",
      text: "Koneksi WhatsApp Gateway akan dimatikan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Ya, Putuskan",
    });

    if (result.isConfirmed) {
      try {
        setIsLoading(true);
        await logoutWhatsapp();
        await fetchStatus();
        Swal.fire({
          icon: "success",
          title: "Terputus",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: "Gagal memutuskan koneksi",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTestSend = (template: WhatsAppTemplate) => {
    const msg = template.content
      .replace("{name}", "Budi")
      .replace("{code}", "TEST-123");
    const url = `https://wa.me/?text=${encodeURIComponent(
      `[TEST SYSTEM]\n${msg}`
    )}`;
    window.open(url, "_blank");
  };

  const isConnected = connStatus.status === "CONNECTED";
  const isReadyForScan = connStatus.status === "QR_READY";
  const isConnecting = connStatus.status === "CONNECTING";

  return (
    <div className="space-y-8 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">WhatsApp Gateway</h2>
        <p className="text-gray-500 text-sm mt-1">
          Kelola koneksi basis wwebjs dan template pesan otomatis.
        </p>
      </div>

      {/* Device Status */}
      <div className="grid md:grid-cols-2 gap-8">
        <div
          className={`rounded-2xl p-6 border-2 relative overflow-hidden transition-all ${
            isConnected
              ? "bg-emerald-50 border-emerald-100"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex items-center gap-4 mb-4 relative z-10">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isConnected
                  ? "bg-emerald-100 text-emerald-600"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              <Smartphone className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Device Status</h3>
              <div
                className={`text-sm font-bold flex items-center gap-1 ${
                  isConnected
                    ? "text-emerald-600"
                    : isConnecting
                    ? "text-amber-500"
                    : "text-gray-400"
                }`}
              >
                {isConnected ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : isConnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                {isConnected
                  ? "Terhubung"
                  : isConnecting
                  ? "Sedang Menghubungkan..."
                  : "Terputus"}
              </div>
            </div>
          </div>

          {isConnected ? (
            <div className="relative z-10">
              <p className="text-sm text-emerald-800 mb-4 bg-emerald-100/50 p-3 rounded-lg border border-emerald-200/50">
                WhatsApp Web Session is <strong>Active</strong>.<br />
                Anda sekarang dapat mengirim pesan otomatis melalui sistem.
              </p>
              <button
                onClick={handleDisconnect}
                className="text-sm text-red-500 font-bold hover:underline inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Logout Session
              </button>
            </div>
          ) : isReadyForScan && connStatus.qr ? (
            <div className="text-center py-4 relative z-10">
              <p className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">
                Scan QR Code dengan WhatsApp Anda
              </p>
              <div className="bg-white p-3 rounded-xl inline-block shadow-md border border-gray-100 mb-4">
                <img
                  src={connStatus.qr}
                  alt="WA QR Code"
                  className="w-40 h-40"
                  loading="lazy"
                />
              </div>
              <p className="text-[10px] text-gray-400 max-w-[200px] mx-auto">
                Buka WhatsApp {">"} Linked Devices {">"} Link a Device
              </p>
            </div>
          ) : (
            <div className="text-center py-10 relative z-10">
              <RefreshCw
                className="w-10 h-10 text-gray-200 animate-spin mx-auto mb-3"
                style={{ animationDuration: "3s" }}
              />
              <p className="text-sm text-gray-400 font-medium mb-4">
                Menyesuaikan server...
              </p>
              <button
                onClick={async () => {
                  setIsLoading(true);
                  await resetWhatsapp();
                  await fetchStatus();
                }}
                className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg hover:bg-gray-200"
              >
                Paksa Reset (Ganti Device)
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm flex flex-col justify-center">
          <h3 className="font-bold text-gray-900 mb-4">Statistik Pesan</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Kapasitas Server</span>
                <span className="font-bold text-gray-900 underline decoration-giz-primary">
                  Local Hosted
                </span>
              </div>
              <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                <div className="bg-emerald-500 h-full rounded-full w-full"></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                <span className="text-xs text-gray-500 uppercase">
                  Provider
                </span>
                <p className="text-sm font-black text-indigo-600">wwebjs.dev</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                <span className="text-xs text-gray-500 uppercase">Latency</span>
                <p className="text-sm font-black text-emerald-600">Stable</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Template Manager */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-giz-primary" /> Template
            Pesan Otomatis
          </h3>
          <button className="text-sm bg-gray-900 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-black flex items-center gap-1">
            <Plus className="w-4 h-4" /> Buat Template
          </button>
        </div>

        <div className="grid gap-4">
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    {tpl.name}
                    <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full uppercase">
                      {tpl.trigger}
                    </span>
                  </h4>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleTestSend(tpl)}
                    className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs font-bold flex items-center gap-1"
                    title="Kirim Tes ke Nomor Sendiri"
                  >
                    <Send className="w-3 h-3" /> Tes
                  </button>
                  <button className="p-1.5 bg-gray-100 text-red-500 rounded hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-dashed border-gray-200 font-mono leading-relaxed">
                {tpl.content}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
